import { useEffect, useState } from 'react'
import { Dialog, Button, Card, Accordion, Text, Icon, Loader } from '@/ui'
import { faDownload } from '@fortawesome/pro-regular-svg-icons'
import { useHelloSignBAA } from './helloSignHelper'
import { useReselector } from '@/utils/sharedHooks'
import { useAnalyticsTrack } from '@/features/amplitude'
import { markUserActionItemCompleteIfExists } from '@/features/user/dashboard/user-action-items/lib/userActionItems.service'
import { SignBAAAgreementStatus } from '@/features/user/dashboard/lib/userActionItemStatuses'
import { TypographyStyles } from '@/styles'
import { getUserActionItemByActionItemIdentifier } from '@/features/user/dashboard/user-action-items/lib/userActionItems.selectors'
import { GuidedOnboardingTaskIdents } from '@/features/onboarding/config'

export const SignBaaModal = ({
  dismissModal,
}: {
  dismissModal: () => void
}) => {
  const [loading, setLoading] = useState(false)
  const track = useAnalyticsTrack()

  const { baa, openSignatureModal, fetching, signUrlError } = useHelloSignBAA()
  const baaActionItem = useReselector(
    getUserActionItemByActionItemIdentifier,
    GuidedOnboardingTaskIdents.SIGN_BAA_AGREEMENT
  )

  useEffect(() => {
    track('viewed sign BAA modal')
  }, [track])

  const handleDownloadBaa = () => {
    if (baa) {
      window.open(baa.fileUrl || '', '_blank')
    }
  }

  const handleClose = async () => {
    track('completed sign BAA modal')
    setLoading(true)
    if (!baaActionItem?.completedAt) {
      await markUserActionItemCompleteIfExists(
        GuidedOnboardingTaskIdents.SIGN_BAA_AGREEMENT,
        track,
        SignBAAAgreementStatus.signed
      )
    }
    setLoading(false)
    dismissModal()
  }

  const renderSigningPrompt = () => (
    <>
      <Dialog.Title>Keep your clients&apos; data safe</Dialog.Title>
      <Dialog.Description className="sr-only">
        This dialog allows the user to sign the BAA agreement.
      </Dialog.Description>
      <Card className="h-60 flex justify-center items-center bg-muted">
        <img
          src="https://heard-images.s3.amazonaws.com/assets/checklist.svg"
          alt="checklist"
        />
      </Card>
      <Text variant={TypographyStyles.BODY_LG} className="mt-4">
        Heard cares about security and your personal data. To continue,
        we&apos;ll need you to sign a <b>Business Associate Agreement (BAA).</b>
      </Text>
      <Text variant={TypographyStyles.BODY_LG} className="mt-4">
        This is a contract that protects your business. It allows us to securely
        handle data that could include sensitive details, such as your
        clients&apos; identifying information.
      </Text>
      <Accordion
        type="single"
        collapsible
        items={[
          {
            title: 'Why does Heard need this document?',
            content:
              "Your clients' personal information may appear in your transactions. We ask that you sign this agreement to ensure that all of this Protected Health Information (PHI) is secure.",
          },
          {
            title: 'What is a Business Associate Agreement?',
            content:
              'A business associate agreement establishes a legally-binding relationship between HIPAA-covered entities and business associates to ensure complete protection of Protected Health Information (PHI).',
          },
        ]}
        className="my-4"
      />
      <Dialog.Footer>
        <Button onClick={openSignatureModal} loading={fetching}>
          View and Sign
        </Button>
      </Dialog.Footer>
    </>
  )

  const renderCompleted = () => (
    <>
      <Dialog.Title>Thanks for signing your BAA</Dialog.Title>
      <Dialog.Description className="sr-only">
        This dialog allows the user to sign the BAA agreement.
      </Dialog.Description>
      <Card className="h-60 flex justify-center items-center bg-green-50 mb-4">
        <img
          src="https://heard-images.s3.amazonaws.com/assets/checklist.svg"
          alt="checklist"
        />
      </Card>
      <Text variant={TypographyStyles.BODY_LG}>
        You can now download a copy of the BAA for your records.
      </Text>
      <div className="flex items-center mt-2">
        <Text variant={TypographyStyles.LABEL_SM}>
          Heard Business Associate Agreement
        </Text>
        {typeof baa !== 'string' && baa?.fileUrl ? (
          <Button onClick={handleDownloadBaa} className="ml-4">
            <Icon icon={faDownload} />
          </Button>
        ) : (
          <Loader active inline className="ml-4" />
        )}
      </div>
      <Dialog.Footer>
        <Button onClick={handleClose} variant="secondary" loading={loading}>
          Close
        </Button>
      </Dialog.Footer>
    </>
  )

  const renderError = () => (
    <>
      <Dialog.Title>Please try again later</Dialog.Title>
      <Dialog.Description className="sr-only">
        Error in signing the BAA agreement.
      </Dialog.Description>
      <Text variant={TypographyStyles.BODY_LG} className="my-4">
        Due to an external error, we can&apos;t currently retrieve the BAA
        document. We&apos;ll ask you to sign it later.
      </Text>
      <Dialog.Footer>
        <Dialog.Close asChild>
          <Button onClick={dismissModal} variant="secondary">
            Close
          </Button>
        </Dialog.Close>
      </Dialog.Footer>
    </>
  )

  return (
    <Dialog open onOpenChange={dismissModal}>
      <Dialog.Content className="max-w-md">
        {signUrlError
          ? renderError()
          : baaActionItem?.completedAt
            ? renderCompleted()
            : renderSigningPrompt()}
      </Dialog.Content>
    </Dialog>
  )
}
